/**
 * Shared controller-alarm → manual troubleshooting lookup (error_codes DB).
 * Standalone helpers — no import from core.ts (avoids circular dependency).
 */

function escHtml(s: string | number | null | undefined): string {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

async function fetchApi(url: string): Promise<any> {
    const res = await fetch(url, { credentials: 'include' });
    const text = await res.text();
    try {
        return text ? JSON.parse(text) : null;
    } catch {
        throw new Error('Răspuns invalid de la server');
    }
}

function formatDateTimeRO(value: string | null | undefined): string {
    if (!value) return '—';
    try {
        const d = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return value;
    }
}

export interface AlarmManualDetails {
    found: boolean;
    machine_id?: string;
    error_code?: string;
    description_ro?: string;
    probable_cause?: string;
    solution?: string;
    severity?: string;
    document_id?: number;
    page_number?: number;
    /** 1-based page for humans (page_number is the 0-based storage index). */
    viewer_page?: number;
    pdf_url?: string;
    matched_code?: string;
    matched_machine_id?: string;
    message?: string;
}

export interface ControllerAlarmModalInput {
    machine_id: string;
    machine_name?: string;
    line_id?: string;
    alarm_code: string;
    alarm_type?: string;
    severity?: string;
    action?: string;
    description_ro?: string;
    cause_ro?: string;
    solution_ro?: string;
    par1?: string | number | null;
    par2?: string | number | null;
    created_at?: string;
    /** EM63: ora din ecranul mașinii (IMM 8 merge cu +60 min față de UTC); `created_at` e în UTC */
    ora_masina?: string;
    /** activă / stinsă la mașină / închisă de noi și de ce — scris de cine deschide (vezi core.ts:_notaInchidere) */
    stare?: string;
    db_id?: number | string;
    title?: string;
    showNavButtons?: boolean;
    onHistory?: () => void;
    onSensors?: () => void;
    onMachine?: () => void;
}

export async function fetchAlarmManualDetails(machineId: string, code: string): Promise<AlarmManualDetails> {
    try {
        const data = await fetchApi(
            `/api/error_codes/lookup?machine_id=${encodeURIComponent(machineId)}&code=${encodeURIComponent(code)}`,
        );
        return data as AlarmManualDetails;
    } catch {
        return {
            found: false,
            message: 'Eroare la căutarea în manual — reîncearcă',
        };
    }
}

function _timeAgoShort(dateStr: string): string {
    try {
        const d = new Date(dateStr + (dateStr.includes('Z') ? '' : 'Z'));
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return 'acum';
        if (diff < 3600) return `${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        return `${Math.floor(diff / 86400)}z`;
    } catch {
        return '';
    }
}

function _detailRow(label: string, value: string): string {
    return `<div class="alert-detail-row"><span class="alert-detail-label">${escHtml(label)}</span><span class="alert-detail-value">${value}</span></div>`;
}

export function buildAlarmManualLoadingHtml(): string {
    return `<div class="alarm-manual-section">
        <h3 class="alarm-manual-title">📖 Din manual</h3>
        <div class="alarm-manual-loading"><span class="typing-dots"><span></span><span></span><span></span></span> Se caută în manual…</div>
    </div>`;
}

export function buildAlarmManualContentHtml(details: AlarmManualDetails, queriedCode?: string): string {
    if (!details.found) {
        return `<div class="alarm-manual-section">
            <h3 class="alarm-manual-title">📖 Din manual</h3>
            <p class="alarm-manual-missing">${escHtml(details.message || 'Nu există în baza de date — verifică codul în HMI sau contactează service')}</p>
        </div>`;
    }

    let decodeNote = '';
    if (queriedCode && details.matched_code && queriedCode !== details.matched_code) {
        decodeNote = `<p class="alarm-manual-decode">Cod HMI <code>${escHtml(queriedCode)}</code> → manual <code>${escHtml(details.matched_code)}</code></p>`;
    }

    let html = `<div class="alarm-manual-section">
        <h3 class="alarm-manual-title">📖 Din manual</h3>
        ${decodeNote}`;

    if (details.description_ro) {
        html += `<div class="alarm-manual-row"><strong>Descriere:</strong> ${escHtml(details.description_ro)}</div>`;
    }
    if (details.probable_cause) {
        html += `<div class="alarm-manual-row"><strong>Cauză:</strong> ${escHtml(details.probable_cause)}</div>`;
    }
    if (details.solution) {
        html += `<div class="alarm-manual-row"><strong>Soluție:</strong> ${escHtml(details.solution)}</div>`;
    }
    if (details.pdf_url) {
        const humanPage = details.viewer_page ?? (typeof details.page_number === 'number' ? details.page_number + 1 : undefined);
        const pageLabel = humanPage ? ` p.${humanPage}` : '';
        html += `<a href="${escHtml(details.pdf_url)}" target="_blank" rel="noopener" class="btn btn-sm btn-outline alarm-manual-pdf">📖 Deschide manual${escHtml(pageLabel)}</a>`;
    }
    html += '</div>';
    return html;
}

export async function loadAlarmManualInto(
    containerId: string,
    machineId: string,
    code: string,
): Promise<AlarmManualDetails> {
    const el = document.getElementById(containerId);
    if (!el) return { found: false };
    el.innerHTML = buildAlarmManualLoadingHtml();
    const details = await fetchAlarmManualDetails(machineId, code);
    el.innerHTML = buildAlarmManualContentHtml(details, code);
    return details;
}

export function closeControllerAlarmModal(): void {
    document.getElementById('modal-controller-alarm')?.remove();
}

export interface AlertDetailModalInput {
    machine_id: string;
    machine_name?: string;
    severity?: string;
    action?: string;
    category?: string;
    interval?: number;
    remaining?: number | null;
    /** 1 nov 2026: textul stării din sursa unică (routes/maintenance/_stare.py) */
    text?: string;
    last_log_date?: string | null;
    last_log_synthetic?: boolean;
    outcome?: string;
    kind: 'maintenance' | 'sensor' | 'open_case';
    onMachine?: () => void;
    onAck?: () => void;
    onDiagnostic?: () => void;
}

/** Detaliul unei alarme care NU vine de la controler.
 *
 * 7 sep 2026 (Dan): în panoul din dreapta doar alarmele de controler se
 * deschideau. Restul — mentenanță scadentă, senzori, cazuri deschise — n-aveau
 * niciun handler de click, iar în coloana îngustă textul e tăiat. Rezultatul:
 * rânduri pe care nu le puteai citi și nici deschide, rămase acolo degeaba.
 */
export function openAlertDetailModal(a: AlertDetailModalInput): void {
    closeControllerAlarmModal();

    const machineName = a.machine_name || a.machine_id;
    const sevLabel = a.kind === 'open_case' ? 'CAZ DESCHIS'
        : (a.severity === 'critical' ? 'CRITIC' : (a.severity === 'info' ? 'INFO' : 'ATENȚIE'));
    const sevClass = a.kind === 'open_case' ? 'warning' : (a.severity || 'warning');
    const titluri: Record<string, string> = {
        maintenance: '🔧 Mentenanță scadentă',
        sensor: '📡 Alarmă de senzor',
        open_case: '📂 Caz deschis',
    };
    const surse: Record<string, string> = {
        maintenance: 'Cadență din manual',
        sensor: 'Senzor utilaj',
        open_case: 'Intervenție nerezolvată',
    };

    let rows = '';
    rows += _detailRow('Sursă', escHtml(surse[a.kind]));
    rows += _detailRow('Utilaj', escHtml(machineName));
    rows += _detailRow('Severitate',
        `<span class="alert-detail-sev inline ${escHtml(sevClass)}">${escHtml(sevLabel)}</span>`);
    if (a.category) rows += _detailRow('Categorie', escHtml(a.category));
    if (a.interval) rows += _detailRow('Cadență', escHtml(`la ${a.interval}h funcționare`));
    if (a.kind === 'maintenance' && a.text) {
        rows += _detailRow('Stare', a.severity === 'critical'
            ? `<strong style="color:var(--danger,#ff5252)">${escHtml(a.text)}</strong>` : escHtml(a.text));
    } else if (a.kind === 'maintenance' && a.remaining != null) {
        rows += _detailRow('Stare', a.remaining <= 0
            ? '<strong style="color:var(--danger,#ff5252)">DEPĂȘIT</strong>'
            : escHtml(`${Math.round(a.remaining)}h rămase`));
    }
    if (a.last_log_date) {
        rows += _detailRow('Ultima lucrare', escHtml(formatDateTimeRO(a.last_log_date))
            + (a.last_log_synthetic ? ' <span style="color:var(--text-muted)">(estimată, fără înregistrare)</span>' : ''));
    } else if (a.kind === 'maintenance') {
        rows += _detailRow('Ultima lucrare', '<span style="color:var(--text-muted)">niciodată înregistrată</span>');
    }
    if (a.outcome) {
        rows += _detailRow('Rezultat', escHtml(a.outcome === 'partial' ? 'Parțial rezolvat' : 'Nerezolvat'));
    }

    // Textul întreg, nu cel tăiat de lățimea coloanei — motivul pentru care
    // există modalul ăsta.
    const corp = a.action
        ? `<div class="alarm-manual-section"><h3 class="alarm-manual-title">📝 Ce cere lucrarea</h3>
             <div class="alarm-manual-row" style="white-space:pre-wrap">${escHtml(a.action)}</div></div>`
        : '';

    const modal = document.createElement('div');
    modal.id = 'modal-controller-alarm';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content alert-detail-modal">
            <h2>${escHtml(titluri[a.kind])}</h2>
            <p class="golive-modal-subtitle">${escHtml(surse[a.kind])} · ${escHtml(machineName)}</p>
            <div class="alert-detail-grid">${rows}</div>
            ${corp}
            <div class="alert-detail-actions">
                ${a.onDiagnostic ? '<button type="button" class="btn btn-green" id="ad-diag">🔍 Vezi cazul</button>' : ''}
                ${a.onMachine ? '<button type="button" class="btn btn-outline" id="ad-machine">🔧 Detalii utilaj</button>' : ''}
                ${a.onAck ? '<button type="button" class="btn btn-outline" id="ad-ack">✓ Confirmă</button>' : ''}
                <button type="button" class="btn btn-outline" id="ad-close">Închide</button>
            </div>
        </div>`;

    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', closeControllerAlarmModal);
    document.getElementById('ad-close')?.addEventListener('click', closeControllerAlarmModal);
    if (a.onDiagnostic) {
        document.getElementById('ad-diag')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onDiagnostic!(); });
    }
    if (a.onMachine) {
        document.getElementById('ad-machine')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onMachine!(); });
    }
    if (a.onAck) {
        document.getElementById('ad-ack')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onAck!(); });
    }
}


export function openControllerAlarmDetailModal(a: ControllerAlarmModalInput): void {
    closeControllerAlarmModal();

    const machineName = a.machine_name || a.machine_id;
    const lineId = a.line_id || '';
    const sevLabel = a.severity === 'critical' ? 'CRITIC' : (a.severity === 'info' ? 'INFO' : 'ATENȚIE');
    const sevClass = a.severity || 'warning';
    const showNav = a.showNavButtons !== false;

    let detailRows = '';
    detailRows += _detailRow('Sursă', escHtml('Controler HMI/PLC'));
    detailRows += _detailRow('Utilaj', escHtml(machineName) + (lineId ? ` <span style="color:var(--text-muted)">(Linia ${escHtml(lineId)})</span>` : ''));
    detailRows += _detailRow('Severitate', `<span class="alert-detail-sev inline ${escHtml(sevClass)}">${escHtml(sevLabel)}</span>`);
    detailRows += _detailRow('Cod alarmă', `<code>${escHtml(String(a.alarm_code))}</code>`);
    if (a.alarm_type) detailRows += _detailRow('Tip', escHtml(String(a.alarm_type)));
    if (a.action) detailRows += _detailRow('Mesaj', escHtml(a.action));
    if (a.par1 || a.par2) {
        detailRows += _detailRow('Parametri', escHtml([a.par1, a.par2].filter(v => v != null && v !== '').join(' · ')));
    }
    if (a.created_at) {
        detailRows += _detailRow('Apărut', escHtml(formatDateTimeRO(a.created_at) + ' (' + _timeAgoShort(a.created_at) + ')'));
    }
    if (a.ora_masina) {
        detailRows += _detailRow('Ora pe ecranul mașinii', escHtml(a.ora_masina.replace('T', ' ')));
    }
    if (a.stare) detailRows += _detailRow('Stare', escHtml(a.stare));

    const modal = document.createElement('div');
    modal.id = 'modal-controller-alarm';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content alert-detail-modal">
            <h2>${escHtml(a.title || '🔔 Alarmă controler')}</h2>
            <p class="golive-modal-subtitle">Controler HMI/PLC · ${escHtml(machineName)}</p>
            <div class="alert-detail-grid">${detailRows}</div>
            <div id="alarm-manual-slot">${buildAlarmManualLoadingHtml()}</div>
            <div class="alert-detail-actions">
                ${showNav ? `<button type="button" class="btn btn-green" id="ca-history">📜 Istoric alarme</button>` : ''}
                ${showNav ? `<button type="button" class="btn btn-outline" id="ca-sensors">📡 Senzori utilaj</button>` : ''}
                ${showNav ? `<button type="button" class="btn btn-outline" id="ca-machine">🔧 Detalii utilaj</button>` : ''}
                <button type="button" class="btn btn-outline" id="ca-close">Închide</button>
            </div>
        </div>`;

    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', closeControllerAlarmModal);
    document.getElementById('ca-close')?.addEventListener('click', closeControllerAlarmModal);

    if (showNav && a.onHistory) {
        document.getElementById('ca-history')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onHistory!(); });
    }
    if (showNav && a.onSensors) {
        document.getElementById('ca-sensors')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onSensors!(); });
    }
    if (showNav && a.onMachine) {
        document.getElementById('ca-machine')?.addEventListener('click', () => { closeControllerAlarmModal(); a.onMachine!(); });
    }

    void loadAlarmManualInto('alarm-manual-slot', a.machine_id, String(a.alarm_code));
}
